/**
 * Animalume - 16タイプ メタデータ（日本語）
 *
 * 各タイプの基本情報を集約。
 * 結果画面・キャラクター表示・SNSシェアで使用。
 */


import type { MbtiType } from '@/features/diagnosis/logic/types';

export type GroupCode = 'NT' | 'NF' | 'SJ' | 'SP';

export type TypeMeta = {
  code: MbtiType;
  nameJa: string;
  groupJa: string;
  groupCode: GroupCode;
  tagline: string;
  essence: string;
  folderName: string;
  strengths: string[];
  relationshipNote: string;
};

export const TYPE_META_JA: Record<MbtiType, TypeMeta> = {
  INTJ: {
    code: 'INTJ',
    nameJa: '建築家',
    groupJa: '光の探究者たち',
    groupCode: 'NT',
    tagline: '未来の地図を、ひとり静かに描く人',
    essence: '本質を見抜き、長期的な計画で世界を構造化する人',
    folderName: '01intj',
    strengths: [
      '本質を見抜き、複雑な状況の中から構造を見出す力',
      '長期的な視点で計画を立て、辛抱強く実行する能力',
      '他者の評価に左右されず、自分の判断軸を持つ独立性',
    ],
    relationshipNote: '人付き合いは広くないけれど深い、というのがINTJの特徴かもしれません。一度信頼した相手には、表面では見せない繊細さや内側にある熱を分かち合える人。\nただ、相手にも同じ深さを求める傾向があるため、表面的な会話だけの関係には早々に飽きてしまうこともあるでしょう。',
  },
  INTP: {
    code: 'INTP',
    nameJa: '論理学者',
    groupJa: '光の探究者たち',
    groupCode: 'NT',
    tagline: '思考の迷路に住む、知的好奇心の探究者',
    essence: '原理と仕組みに惹かれ、純粋な理解を追い求める人',
    folderName: '02intp',
    strengths: [
      '複雑な問題を論理的に分解し、本質的な矛盾を発見する力',
      '既存の前提を疑い、新しい視点から物事を捉え直す能力',
      '知識を体系化し、広い分野を横断して学ぶ知的好奇心',
    ],
    relationshipNote: '深い知的対話ができる相手を自然と引き寄せる傾向があります。感情表現は少なく見えることもありますが、内側では豊かな思考世界を持っており、本当に信頼した人にはそれを開いていく面があります。',
  },
  ENTJ: {
    code: 'ENTJ',
    nameJa: '指揮官',
    groupJa: '光の探究者たち',
    groupCode: 'NT',
    tagline: '道を切り開き、ビジョンを実現する人',
    essence: '決断力とリーダーシップで、自ら未来を形にする人',
    folderName: '03entj',
    strengths: [
      '方向性を示し、周囲を目標に向かって動かすリーダーシップ',
      '問題の本質を素早く把握し、決断し実行に移す行動力',
      '長期ビジョンを描きながら、短期の課題も同時に管理する戦略的思考',
    ],
    relationshipNote: '相手への要求水準が高めになる傾向があり、それが時にプレッシャーに映ることもあるかもしれません。けれど根底には、相手への深い信頼と期待があります。対等に議論できる相手との関係に、特に充実感を感じる面があります。',
  },
  ENTP: {
    code: 'ENTP',
    nameJa: '討論者',
    groupJa: '光の探究者たち',
    groupCode: 'NT',
    tagline: '閃きを連鎖させ、新しい視点で世界を照らす人',
    essence: '可能性を発見し、既成概念に揺さぶりをかける人',
    folderName: '04entp',
    strengths: [
      '物事の多面性を瞬時に把握し、独創的なアイデアを生み出す発想力',
      '議論の中で思考を深め、新しい視点を発見する知的な俊敏さ',
      '変化を恐れず、未知の状況にも柔軟に対応する適応力',
    ],
    relationshipNote: '知的な刺激のある関係に活力を見出す傾向があります。時に議論好きが行き過ぎて相手を疲れさせることもありますが、それは本質的な誠実さと好奇心の表れ。感情の言語を意識的に使うことで、関係がさらに豊かになる面があります。',
  },
  INFJ: {
    code: 'INFJ',
    nameJa: '提唱者',
    groupJa: '光を編む人たち',
    groupCode: 'NF',
    tagline: '月の番人、人の魂を見つめる静かな洞察者',
    essence: '内なる宇宙で人の本質を見つめ、深く理解する人',
    folderName: '05infj',
    strengths: [
      '言葉にならない感情や動機を感じ取る、深い直観的な洞察力',
      '長期的な視点でビジョンを描き、人を巻き込みながら実現する力',
      '理想への強い信念を持ちながら、現実との橋渡しをする実践力',
    ],
    relationshipNote: '人数は少なくても、深くつながることを大切にする傾向があります。相手の本音を読み取る感受性が強いため、表面的な関係には満足しにくい面も。自分の内面世界を打ち明けられる相手との関係に、特別な充実感を感じるでしょう。',
  },
  INFP: {
    code: 'INFP',
    nameJa: '仲介者',
    groupJa: '光を編む人たち',
    groupCode: 'NF',
    tagline: '詩を書く人、繊細な心で世界の美を集める',
    essence: '内なる価値観に忠実に、美しさと意味を求める人',
    folderName: '06infp',
    strengths: [
      '美しさや意味を感じ取る、繊細かつ豊かな感受性',
      '自分の価値観に誠実に生き、それを静かに表現する真摯さ',
      '他者の痛みや感情に深く寄り添い、本質的な共感を示す力',
    ],
    relationshipNote: '真剣に向き合うことで、深い絆を育む傾向があります。表面的にはシャイに見えることもありますが、価値観を共有できる相手には豊かな内面世界を開いていく面があります。自分を飾らずにいられる関係に、最も安らぎを感じるでしょう。',
  },
  ENFJ: {
    code: 'ENFJ',
    nameJa: '主人公',
    groupJa: '光を編む人たち',
    groupCode: 'NF',
    tagline: '人を温かく包む光、場を照らす存在',
    essence: '共感と情熱で人を導き、可能性を引き出す人',
    folderName: '07enfj',
    strengths: [
      '人の可能性を直観的に感じ取り、成長を引き出す共感力',
      '周囲の雰囲気を読みながら、自然に人を導くコミュニケーション力',
      '大きなビジョンを持ち、チームを熱意で動かすリーダーシップ',
    ],
    relationshipNote: '相手への関心と思いやりが自然に滲み出る傾向があります。周囲の感情に敏感なため、時に他者の問題を抱えすぎてしまうことも。自分自身のケアも同じくらい大切にすることで、より豊かな関係を長く育てていける面があります。',
  },
  ENFP: {
    code: 'ENFP',
    nameJa: '運動家',
    groupJa: '光を編む人たち',
    groupCode: 'NF',
    tagline: '風と踊る人、生命力の祝祭',
    essence: '好奇心と熱意で、人と人、アイデアを繋ぐ人',
    folderName: '08enfp',
    strengths: [
      '人やアイデアの可能性を発見し、熱意を持って伝える表現力',
      'どんな状況にも新鮮さを見出し、場に活力を与える感受性',
      '多様な人との関係を大切にし、それぞれの個性を引き出す社交性',
    ],
    relationshipNote: '人との出会いに深い喜びを感じる傾向があります。感情が豊かで人を引きつける反面、時に気持ちが散りやすくなることも。「この人とだけ」と思える深い絆に、特別な充実感を感じる面があるでしょう。',
  },
  ISTJ: {
    code: 'ISTJ',
    nameJa: '管理者',
    groupJa: '光の番人たち',
    groupCode: 'SJ',
    tagline: '秩序を守る人、誠実さの番人',
    essence: '責任と一貫性で、信頼の土台を築く人',
    folderName: '09istj',
    strengths: [
      '細部まで丁寧に確認し、確実に物事を完結させる責任感',
      '長年かけて培った知識と経験を、着実に積み重ねる継続力',
      '約束を守り、揺るぎない信頼性で周囲の安心感を生み出す誠実さ',
    ],
    relationshipNote: '急には打ち解けなくても、一度信頼関係が築かれると非常に安定した絆を育む傾向があります。感情を大きく表現するのは得意でないかもしれませんが、行動で誠実さを示す人。長期的に寄り添える関係に、深い充実感を感じるでしょう。',
  },
  ISFJ: {
    code: 'ISFJ',
    nameJa: '擁護者',
    groupJa: '光の番人たち',
    groupCode: 'SJ',
    tagline: '温かい部屋を守る人、静かに人を支える',
    essence: '思いやりと細やかさで、大切な人を守る人',
    folderName: '10isfj',
    strengths: [
      '相手の細かなニーズや感情の変化を敏感に察知する気配り力',
      '安定した環境を作り上げ、静かに人を支え続ける温かさ',
      '過去の経験と学びを大切にし、現実的に物事を進める堅実さ',
    ],
    relationshipNote: '目立たない場所から、人を支えることに喜びを感じる傾向があります。自分より相手を優先しすぎてしまうこともあるかもしれませんが、その深い思いやりは確かに人の心に届きます。感謝の言葉を素直に受け取ることで、関係がさらに豊かになる面があります。',
  },
  ESTJ: {
    code: 'ESTJ',
    nameJa: '幹部',
    groupJa: '光の番人たち',
    groupCode: 'SJ',
    tagline: '構造を作る人、社会の基盤を組み立てる',
    essence: '組織化と実行力で、秩序ある世界を作る人',
    folderName: '11estj',
    strengths: [
      '物事を体系的に整理し、効率よく実行に移す組織力',
      '責任ある立場で判断を下し、周囲を引っ張るリーダーシップ',
      '一貫した基準と誠実さで、信頼される存在となる安定感',
    ],
    relationshipNote: '明確さと公正さを大切にする傾向があります。感情表現よりも行動で愛情を示すタイプかもしれません。相手の自立性を尊重しながらも、困ったときには頼れる存在として関係を支えていく面があります。',
  },
  ESFJ: {
    code: 'ESFJ',
    nameJa: '領事官',
    groupJa: '光の番人たち',
    groupCode: 'SJ',
    tagline: '人と人を繋ぐ人、もてなしの心',
    essence: '調和と気配りで、人々の絆を育む人',
    folderName: '12esfj',
    strengths: [
      '周囲の雰囲気や感情の流れを敏感に読み取り、場を和やかにする力',
      '人のニーズに気づき、先回りして支援する温かいホスピタリティ',
      '長く続く人間関係を大切に育て、コミュニティの絆を深める社交性',
    ],
    relationshipNote: '周囲との調和を心がけ、人を喜ばせることに深い充実感を感じる傾向があります。時に自分の気持ちを後回しにしがちなこともありますが、その温かさは確かに周囲の人に伝わっています。自分のニーズも大切にすることで、より長く豊かな関係を育てられる面があります。',
  },
  ISTP: {
    code: 'ISTP',
    nameJa: '巨匠',
    groupJa: '光の踊り手たち',
    groupCode: 'SP',
    tagline: '静かな職人、手で世界を理解する',
    essence: '実践と分析で、物事の仕組みを解き明かす人',
    folderName: '13istp',
    strengths: [
      '物事の構造を手と思考で解き明かす、実践的な問題解決力',
      '感情に流されず、状況を冷静に分析して最適解を見つける論理性',
      '必要なときに素早く行動し、限られた条件でも成果を出す適応力',
    ],
    relationshipNote: '言葉より行動で信頼を表す傾向があります。深い感情表現は少なく見えることもありますが、大切にしている相手には、実際の行動や関与で誠実さを示していく面があります。互いの独立性を尊重できる関係に、特に安心感を感じるでしょう。',
  },
  ISFP: {
    code: 'ISFP',
    nameJa: '冒険家',
    groupJa: '光の踊り手たち',
    groupCode: 'SP',
    tagline: '感性で世界を描く人、内なる詩を持つ',
    essence: '繊細な感受性で、美しさを表現する人',
    folderName: '14isfp',
    strengths: [
      '世界の美しさや細部に宿る価値を感じ取る、繊細な感受性',
      '自分のペースと価値観を守りながら、静かに存在する誠実さ',
      '今この瞬間の体験を大切にし、感覚を通じて深く関わる能力',
    ],
    relationshipNote: '言葉より体験や共有の時間で絆を育む傾向があります。表面からは読みにくくても、内側には豊かな感情の世界があります。自分らしさを認めてもらえる関係に、最も深い安らぎと喜びを感じるでしょう。',
  },
  ESTP: {
    code: 'ESTP',
    nameJa: '起業家',
    groupJa: '光の踊り手たち',
    groupCode: 'SP',
    tagline: 'チャンスを掴む人、決断の瞬間に生きる',
    essence: '行動力と適応力で、今この瞬間を切り拓く人',
    folderName: '15estp',
    strengths: [
      '状況を瞬時に読み取り、即座に最善の行動を取る実践的な判断力',
      'リスクを恐れず、チャンスに飛び込む大胆さと行動力',
      'どんな場でも自然にリードし、周囲に活力を与えるエネルギー',
    ],
    relationshipNote: '直接的でエネルギッシュな関係に充実感を感じる傾向があります。感情の深みより刺激や共有の体験を重視することもありますが、一度大切と思った人には驚くほど誠実な面があります。関係に正直さと率直さを持ち込むことを大切にしています。',
  },
  ESFP: {
    code: 'ESFP',
    nameJa: 'エンターテイナー',
    groupJa: '光の踊り手たち',
    groupCode: 'SP',
    tagline: '場を祝祭にする人、今この瞬間を生きる',
    essence: '陽気さと社交性で、人々に喜びを届ける人',
    folderName: '16esfp',
    strengths: [
      '場の空気を読み取り、自然に人を笑顔にする陽気さと親しみやすさ',
      '今この瞬間を全力で楽しみ、周囲を巻き込む生命力',
      '人の感情に素直に反応し、共感と温かさを惜しみなく表現する力',
    ],
    relationshipNote: '人と一緒にいることに純粋な喜びを感じる傾向があります。深く考えるより感じることを優先する場面もありますが、大切な人への気持ちは行動と笑顔で確かに伝わっています。心から楽しめる時間と相手との関係に、特別な充実感を感じるでしょう。',
  },
};
